import { useEffect, useState } from "react";
import { api, ADMIN_FLAG } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, LogOut, Users, Mail, MapPin, Baby, Store, Copy, Check, RefreshCw, Download, Bell, MessageCircle, Package, Ruler } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShopifyStatus } from "@/components/ShopifyStatus";
import { ProductOverrides } from "@/components/ProductOverrides";
import { ChangePassword } from "@/components/ChangePassword";
import { InfluencerAdmin } from "@/components/InfluencerAdmin";
import { CuratedAdmin } from "@/components/CuratedAdmin";
import { PackageQR } from "@/components/PackageQR";
import { MONTHS_IT } from "@/lib/api";

function formatErr(detail) {
  if (!detail) return "Errore";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg).join(" ");
  return String(detail);
}

function CopyBlock({ code, label, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Codice copiato!");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-[#5C534E]">{label}</span>
        <button data-testid={testid} onClick={copy}
          className="inline-flex items-center gap-1 text-xs rounded-full bg-[#2D2825] text-white px-3 py-1">
          {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copiato" : "Copia"}
        </button>
      </div>
      <pre className="text-[11px] bg-[#FAF8F5] border border-[#EAE3DC] rounded-xl p-3 overflow-x-auto text-[#5C534E] whitespace-pre-wrap break-all">{code}</pre>
    </div>
  );
}

function ShopifyIntegration() {
  const appUrl = window.location.origin;
  const dashUrl = `${appUrl}/admin`;
  const buttonSnippet =
    `<a href="${appUrl}" target="_blank" rel="noopener"\n   style="display:inline-block;background:#E5A8A0;color:#fff;padding:14px 30px;\n   border-radius:999px;text-decoration:none;font-family:sans-serif;font-size:16px">\n  Crea la tua Lista Nascita \u{1F90D}\n</a>`;
  const iframeSnippet =
    `<iframe src="${appUrl}" title="Minime Baby - Lista Nascita"\n  style="width:100%;height:1000px;border:0;border-radius:16px" loading="lazy"></iframe>`;

  return (
    <div className="rounded-2xl bg-white border border-[#EAE3DC] p-6" data-testid="shopify-integration">
      <div className="flex items-center gap-2 mb-1">
        <Store size={18} className="text-[#4E7363]" />
        <h3 className="font-display font-medium text-[#2D2825]">Integra con il tuo shop Shopify</h3>
      </div>
      <p className="text-sm text-[#8C7F78] mb-5">
        Collega questo servizio a <strong>minimebaby.it</strong>: aggiungi un pulsante, incorpora la pagina
        o inserisci il link nel menù del negozio. Indirizzo del servizio:{" "}
        <span className="text-[#C87D75] break-all">{appUrl}</span>
      </p>

      <div className="mb-6 rounded-xl bg-[#EAF0EC] border border-[#A7C4B5] p-4" data-testid="dashboard-access-block">
        <div className="text-sm text-[#2D2825] font-display font-medium mb-1">Indirizzo della tua Dashboard</div>
        <p className="text-xs text-[#5C534E] mb-3">
          Accedi SEMPRE alla dashboard da questo indirizzo (lo stesso dominio del sito pubblicato).
          I dati delle clienti vivono qui.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <code data-testid="dashboard-url" className="text-xs bg-white border border-[#A7C4B5] rounded-lg px-3 py-1.5 text-[#4E7363] break-all">{dashUrl}</code>
          <button data-testid="copy-dashboard-url" onClick={() => { navigator.clipboard.writeText(dashUrl); toast.success("Indirizzo dashboard copiato!"); }}
            className="inline-flex items-center gap-1 text-xs rounded-full bg-[#4E7363] text-white px-3 py-1.5 hover:bg-[#3d5a4e] transition-colors">
            <Copy size={12} /> Copia
          </button>
          <a data-testid="open-dashboard-link" href={dashUrl}
            className="inline-flex items-center gap-1 text-xs rounded-full border border-[#4E7363] text-[#4E7363] px-3 py-1.5 hover:bg-white transition-colors">
            Apri Dashboard
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CopyBlock testid="copy-button-snippet"
          label="1. Pulsante da incollare in una pagina Shopify (sezione HTML personalizzato)"
          code={buttonSnippet} />
        <CopyBlock testid="copy-iframe-snippet"
          label="2. Incorpora l'intera esperienza in una pagina (iframe)"
          code={iframeSnippet} />
      </div>

      <div className="mt-6 rounded-xl bg-[#FDF6E7] border border-[#F2D293] p-4 text-sm text-[#7a5f1f]">
        <strong>3. Aggiungi al menù di Shopify:</strong> Shopify Admin → <em>Negozio online → Navigazione</em> →
        scegli il menù → <em>Aggiungi voce</em> → incolla il link <span className="break-all">{appUrl}</span> con
        etichetta “Lista Nascita”. Poi <em>Salva</em>.
      </div>
      <p className="mt-3 text-xs text-[#8C7F78]">
        Nota: per avere un indirizzo pubblico stabile pubblica l'app (Deploy). Dopo la pubblicazione questi snippet
        useranno automaticamente il nuovo indirizzo.
      </p>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [catalogOv, setCatalogOv] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null); // {html, subject, recipient}
  const [previewLoading, setPreviewLoading] = useState(false);

  const openPreview = async (profileId) => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/admin/monthly-tips-preview",
        { params: profileId ? { profile_id: profileId } : {} });
      setPreview(data);
    } catch {
      toast.error("Anteprima non disponibile. Riprova.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const load = async (notify = false) => {
    try {
      const [s, p] = await Promise.all([api.get("/admin/stats"), api.get("/admin/profiles")]);
      setStats(s.data);
      setProfiles(p.data.profiles);
      if (notify) toast.success(`Elenco aggiornato · ${p.data.profiles.length} mamme`);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sessione scaduta. Accedi di nuovo.");
      } else {
        toast.error("Impossibile caricare i dati. Riprova.");
      }
      logout();
      return;
    }
    // Catalog overview is independent and may be slow (Shopify refetch) — don't block the table.
    api.get("/admin/catalog-overview").then(({ data }) => setCatalogOv(data)).catch(() => {});
  };

  const refreshCatalog = async () => {
    setBusy("catalog");
    try {
      await api.post("/admin/refresh-catalog");
      toast.success("Aggiornamento catalogo avviato…");
      setTimeout(async () => {
        try {
          const c = await api.get("/admin/catalog-overview");
          setCatalogOv(c.data);
          toast.success("Panoramica catalogo aggiornata");
        } catch (err) {
          console.error("catalog overview refresh failed", err);
          toast.error("Panoramica catalogo non aggiornata. Riprova più tardi.");
        }
        setBusy("");
      }, 8000);
    } catch {
      toast.error("Aggiornamento non riuscito");
      setBusy("");
    }
  };

  const sendReminder = async (pid) => {
    setBusy("reminder-" + pid);
    try {
      const { data } = await api.post(`/admin/profiles/${pid}/send-reminder`);
      toast.success(`Promemoria inviato (${Math.max(data.days, 0)} giorni)`);
    } catch {
      toast.error("Invio promemoria non riuscito");
    } finally {
      setBusy("");
    }
  };

  const exportCsv = () => {
    const head = ["Nome", "Bimbo", "Email", "WhatsApp", "Citta", "Nascita", "Sesso", "Taglia", "ListaInviata", "Capi"];
    const rows = profiles.map((p) => [
      p.name, p.baby_name || "", p.email, p.whatsapp || "",
      p.city,
      `${p.due_date?.day}/${p.due_date?.month}/${p.due_date?.year}`,
      p.sex, p.recommended_size?.label || "",
      p.list_sent ? "si" : "no", (p.wishlist || []).length,
    ]);
    const csv = [head, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `minime-clienti-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success("CSV esportato");
  };

  const waLink = (num) => `https://wa.me/${String(num || "").replace(/\D/g, "")}`;

  useEffect(() => {
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => { localStorage.removeItem(ADMIN_FLAG); setAuthed(false); });
  }, []);
  useEffect(() => { if (authed) load(); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", creds);
      localStorage.setItem(ADMIN_FLAG, "1");
      setAuthed(true);
      toast.success(`Benvenuto, ${data.name}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem(ADMIN_FLAG);
    setAuthed(false);
    setStats(null);
  };

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] text-[#8C7F78]" data-testid="admin-checking">Caricamento…</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] px-4">
        <form onSubmit={login} data-testid="admin-login-form"
          className="w-full max-w-sm rounded-2xl bg-white border border-[#EAE3DC] p-8 shadow-sm">
          <h1 className="font-serif text-3xl text-[#2D2825] text-center">Area riservata</h1>
          <p className="text-center text-[#8C7F78] text-sm mt-1 mb-6">Minime Baby · Dashboard</p>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">Email</Label>
              <Input data-testid="admin-email" type="email" value={creds.email}
                onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
                className="rounded-xl border-[#EAE3DC]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#5C534E]">Password</Label>
              <Input data-testid="admin-password" type="password" value={creds.password}
                onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                className="rounded-xl border-[#EAE3DC]" />
            </div>
            <Button data-testid="admin-login-button" type="submit" disabled={loading}
              className="w-full rounded-full bg-[#2D2825] text-white h-11">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Accedi"}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  const kpis = [
    { label: "Mamme iscritte", value: stats?.total_mothers ?? "—", icon: Users, color: "#C87D75" },
    { label: "Liste inviate", value: stats?.lists_sent ?? "—", icon: Mail, color: "#4E7363" },
    { label: "Città", value: stats?.top_cities?.length ?? "—", icon: MapPin, color: "#4A7496" },
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="bg-white border-b border-[#EAE3DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <span className="font-serif text-2xl text-[#2D2825]">Minime Baby · Admin</span>
          <div className="flex items-center gap-5">
            <ChangePassword />
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm text-[#8C7F78] hover:text-[#2D2825]">
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-2xl text-[#2D2825]">Pannello di controllo</h2>
          <div className="flex flex-wrap gap-2">
            <button data-testid="admin-reload" onClick={() => load(true)} disabled={busy === "catalog"}
              className="inline-flex items-center gap-2 rounded-full border border-[#D2C5B9] text-[#5C534E] hover:bg-white px-4 py-2 text-sm transition-colors">
              <Users size={15} /> Ricarica clienti
            </button>
            <button data-testid="admin-refresh-catalog" onClick={refreshCatalog} disabled={busy === "catalog"}
              className="inline-flex items-center gap-2 rounded-full border border-[#D2C5B9] text-[#5C534E] hover:bg-white px-4 py-2 text-sm transition-colors">
              {busy === "catalog" ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />} Aggiorna catalogo
            </button>
            <button data-testid="admin-preview-email" onClick={() => openPreview(null)} disabled={previewLoading}
              className="inline-flex items-center gap-2 rounded-full border border-[#D2C5B9] text-[#5C534E] hover:bg-white px-4 py-2 text-sm transition-colors">
              {previewLoading ? <Loader2 className="animate-spin" size={15} /> : <Mail size={15} />} Anteprima email mensile
            </button>
            <button data-testid="admin-export-csv" onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-full bg-[#2D2825] text-white hover:bg-[#473f3a] px-4 py-2 text-sm transition-colors">
              <Download size={15} /> Esporta CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="admin-kpis">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-2xl bg-white border border-[#EAE3DC] p-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#8C7F78]">{k.label}</span>
                <k.icon size={20} style={{ color: k.color }} />
              </div>
              <div className="font-serif text-4xl text-[#2D2825] mt-2">{k.value}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-white border border-[#EAE3DC] p-6">
            <h3 className="font-display font-medium text-[#2D2825] mb-4">Prodotti più richiesti</h3>
            {stats?.top_products?.length ? stats.top_products.map((p) => (
              <div key={p.title} className="flex justify-between text-sm py-1.5 border-b border-[#F4EFEA] last:border-0">
                <span className="text-[#5C534E] truncate pr-3">{p.title}</span>
                <span className="text-[#C87D75] font-semibold">{p.count}</span>
              </div>
            )) : <p className="text-sm text-[#8C7F78]">Nessun dato ancora.</p>}
          </div>
          <div className="rounded-2xl bg-white border border-[#EAE3DC] p-6">
            <h3 className="font-display font-medium text-[#2D2825] mb-4">Città più frequenti</h3>
            {stats?.top_cities?.length ? stats.top_cities.map((c) => (
              <div key={c.city} className="flex justify-between text-sm py-1.5 border-b border-[#F4EFEA] last:border-0">
                <span className="text-[#5C534E]">{c.city}</span>
                <span className="text-[#4A7496] font-semibold">{c.count}</span>
              </div>
            )) : <p className="text-sm text-[#8C7F78]">Nessun dato ancora.</p>}
          </div>
        </div>

        <ShopifyStatus />
        <PackageQR />
        <InfluencerAdmin />
        <CuratedAdmin />
        <ProductOverrides />
        {catalogOv && (
          <div className="rounded-2xl bg-white border border-[#EAE3DC] p-6" data-testid="catalog-overview">
            <div className="flex items-center gap-2 mb-4">
              <Package size={18} className="text-[#4E7363]" />
              <h3 className="font-display font-medium text-[#2D2825]">Panoramica catalogo (live da Shopify)</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              <div className="rounded-xl bg-[#FAF8F5] p-4">
                <div className="text-xs text-[#8C7F78]">Prodotti totali</div>
                <div className="font-serif text-3xl text-[#2D2825]">{catalogOv.total}</div>
              </div>
              <div className="rounded-xl bg-[#EAF0EC] p-4">
                <div className="text-xs text-[#4E7363]">Disponibili</div>
                <div className="font-serif text-3xl text-[#4E7363]">{catalogOv.available}</div>
              </div>
              <div className="rounded-xl bg-[#F9ECE9] p-4">
                <div className="text-xs text-[#C87D75]">Femmina</div>
                <div className="font-serif text-3xl text-[#C87D75]">{catalogOv.by_gender?.femmina || 0}</div>
              </div>
              <div className="rounded-xl bg-[#EBF3F8] p-4">
                <div className="text-xs text-[#4A7496]">Maschio</div>
                <div className="font-serif text-3xl text-[#4A7496]">{catalogOv.by_gender?.maschio || 0}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(catalogOv.by_category || {}).map(([c, n]) => (
                <span key={c} className="rounded-full bg-[#F4EFEA] text-[#5C534E] text-xs px-3 py-1">{c}: {n}</span>
              ))}
            </div>
            {catalogOv.season_sources && (
              <p className="mt-4 text-xs text-[#8C7F78]" data-testid="catalog-season-sources">
                Stagione da tag Shopify: <strong className="text-[#4E7363]">{catalogOv.season_sources.shopify}</strong> prodotti ·
                da AI: <strong>{catalogOv.season_sources.ai}</strong> · da tessuto: <strong>{catalogOv.season_sources.materiale}</strong>.
                Aggiungi in Shopify i tag Primavera, Estate, Autunno, Inverno, "Primavera-Estate", "Autunno-Inverno" o "Tutte le stagioni" per controllare tu la stagione.
              </p>
            )}
          </div>
        )}

        <div className="rounded-2xl bg-white border border-[#EAE3DC] overflow-hidden">
          <div className="p-6 pb-3 flex items-center gap-2">
            <Baby size={18} className="text-[#C87D75]" />
            <h3 className="font-display font-medium text-[#2D2825]">Mamme iscritte ({profiles.length})</h3>
            <span className="text-xs text-[#8C7F78] ml-auto">Clicca una riga per il dettaglio</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-profiles-table">
              <thead className="bg-[#FAF8F5] text-[#8C7F78] text-left">
                <tr>
                  <th className="px-6 py-3 font-medium">Nome</th>
                  <th className="px-6 py-3 font-medium">Bimbo</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">WhatsApp</th>
                  <th className="px-6 py-3 font-medium">Città</th>
                  <th className="px-6 py-3 font-medium">Nascita</th>
                  <th className="px-6 py-3 font-medium">Sesso</th>
                  <th className="px-6 py-3 font-medium">Taglia</th>
                  <th className="px-6 py-3 font-medium">Lista</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p.id} onClick={() => setDetail(p)} data-testid={`admin-row-${p.id}`}
                    className="border-t border-[#F4EFEA] cursor-pointer hover:bg-[#FAF8F5] transition-colors">
                    <td className="px-6 py-3 text-[#2D2825]">{p.name}</td>
                    <td className="px-6 py-3 text-[#5C534E]" data-testid={`admin-baby-name-${p.id}`}>{p.baby_name || "—"}</td>
                    <td className="px-6 py-3 text-[#5C534E]">{p.email}</td>
                    <td className="px-6 py-3 text-[#5C534E]">{p.whatsapp || "—"}</td>
                    <td className="px-6 py-3 text-[#5C534E]">{p.city}</td>
                    <td className="px-6 py-3 text-[#5C534E]">
                      {p.due_date?.day} {MONTHS_IT[(p.due_date?.month || 1) - 1]} {p.due_date?.year}
                    </td>
                    <td className="px-6 py-3 text-[#5C534E] capitalize">{p.sex}</td>
                    <td className="px-6 py-3 text-[#5C534E]">{p.recommended_size?.label}</td>
                    <td className="px-6 py-3">
                      {p.list_sent
                        ? <span className="text-[#4E7363]">✓ inviata</span>
                        : <span className="text-[#8C7F78]">{(p.wishlist || []).length} items</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <ShopifyIntegration />
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="admin-detail-modal" className="sm:max-w-2xl bg-[#FAF8F5] border-[#EAE3DC] max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2D2825]">{detail.name}</DialogTitle>
                <DialogDescription className="text-[#8C7F78]">Scheda cliente e lista nascita</DialogDescription>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-white border border-[#EAE3DC] p-3">
                    <div className="text-xs text-[#8C7F78] flex items-center gap-1"><Mail size={12} /> Email</div>
                    <div className="text-[#2D2825] break-all">{detail.email}</div>
                  </div>
                  <div className="rounded-xl bg-white border border-[#EAE3DC] p-3">
                    <div className="text-xs text-[#8C7F78] flex items-center gap-1"><MapPin size={12} /> Città</div>
                    <div className="text-[#2D2825]">{detail.city}</div>
                  </div>
                  <div className="rounded-xl bg-white border border-[#EAE3DC] p-3">
                    <div className="text-xs text-[#8C7F78] flex items-center gap-1"><Baby size={12} /> Nascita · Sesso</div>
                    <div className="text-[#2D2825] capitalize">
                      {detail.due_date?.day} {MONTHS_IT[(detail.due_date?.month || 1) - 1]} {detail.due_date?.year} · {detail.sex}{detail.baby_name ? ` · ${detail.baby_name}` : ""}
                    </div>
                  </div>
                  <div className="rounded-xl bg-white border border-[#EAE3DC] p-3">
                    <div className="text-xs text-[#8C7F78] flex items-center gap-1"><Ruler size={12} /> Taglia consigliata</div>
                    <div className="text-[#2D2825]">{detail.recommended_size?.label}</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <a href={waLink(detail.whatsapp)} target="_blank" rel="noreferrer" data-testid="admin-whatsapp-link"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2 text-sm hover:opacity-90">
                    <MessageCircle size={15} /> WhatsApp
                  </a>
                  <button onClick={() => sendReminder(detail.id)} disabled={busy === "reminder-" + detail.id}
                    data-testid="admin-send-reminder"
                    className="inline-flex items-center gap-2 rounded-full bg-[#E5A8A0] text-white px-4 py-2 text-sm hover:bg-[#C87D75] disabled:opacity-60">
                    {busy === "reminder-" + detail.id ? <Loader2 className="animate-spin" size={15} /> : <Bell size={15} />} Invia promemoria
                  </button>
                  <a href={`${window.location.origin}/gift/${detail.share_token}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-[#A7C4B5] text-[#4E7363] px-4 py-2 text-sm hover:bg-[#EAF0EC]">
                    Pagina regalo
                  </a>
                </div>

                <div className="rounded-xl bg-white border border-[#EAE3DC] p-4">
                  <h4 className="font-display font-medium text-[#2D2825] mb-2">
                    Lista nascita ({(detail.wishlist || []).length}) {detail.list_sent && <span className="text-[#4E7363] text-sm">· inviata ✓</span>}
                  </h4>
                  {(detail.wishlist || []).length === 0 ? (
                    <p className="text-sm text-[#8C7F78]">Nessun prodotto in lista.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-52 overflow-y-auto">
                      {detail.wishlist.map((it) => (
                        <div key={it.product_id} className="flex justify-between text-sm border-b border-[#F4EFEA] last:border-0 py-1.5">
                          <span className="text-[#5C534E] truncate pr-3">{it.title}</span>
                          <span className="text-[#8C7F78] whitespace-nowrap">{it.size || "-"} · x{it.quantity} · € {it.price}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-xl bg-white border border-[#EAE3DC] p-4">
                  <h4 className="font-display font-medium text-[#2D2825] mb-2">Rilevamenti crescita ({(detail.growth_records || []).length})</h4>
                  {(detail.growth_records || []).length === 0 ? (
                    <p className="text-sm text-[#8C7F78]">Nessun rilevamento.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {detail.growth_records.map((r) => (
                        <span key={r.month} className="rounded-full bg-[#F4EFEA] text-[#5C534E] text-xs px-3 py-1">
                          {r.month}m: {r.weight ? `${r.weight}kg` : ""} {r.length ? `${r.length}cm` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="email-preview-dialog" className="sm:max-w-2xl bg-[#FAF8F5] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2D2825]">Anteprima email mensile</DialogTitle>
            <DialogDescription className="text-[#8C7F78]">
              {preview?.subject}{preview?.recipient ? ` · per ${preview.recipient}` : " · esempio"}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden rounded-xl border border-[#EAE3DC] bg-white">
            <iframe data-testid="email-preview-iframe" title="Anteprima email" srcDoc={preview?.html || ""}
              className="w-full h-[62vh]" sandbox="" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
